import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Filter, Trash2, Eye, Download, Upload, FileText, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { documentService } from '../../services/documentService'
import { caseService } from '../../services/caseService'
import { Document } from '../../types/document'

export default function Documents() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)

  // Fetch cases for dropdown
  const { data: casesData } = useQuery({
    queryKey: ['cases'],
    queryFn: () => caseService.listCases(),
  })

  // Fetch documents for selected case
  const { data: documents, isLoading, error, refetch } = useQuery({
    queryKey: ['documents', selectedCaseId],
    queryFn: () => selectedCaseId ? documentService.listDocuments(selectedCaseId) : Promise.resolve([]),
    enabled: !!selectedCaseId,
  })

  // Auto-select first case if available
  useEffect(() => {
    if (casesData?.cases && casesData.cases.length > 0 && !selectedCaseId) {
      setSelectedCaseId(casesData.cases[0].id)
    }
  }, [casesData, selectedCaseId])

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCaseId || !uploadFile) throw new Error('Missing case or file')
      return documentService.uploadDocument(
        selectedCaseId,
        uploadTitle,
        uploadDescription,
        uploadFile,
        (progress) => setUploadProgress(progress)
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedCaseId] })
      setIsUploadModalOpen(false)
      setUploadFile(null)
      setUploadTitle('')
      setUploadDescription('')
      setUploadProgress(0)
      toast.success('Document uploaded successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to upload document')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await documentService.deleteDocument(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', selectedCaseId] })
      setIsDeleteModalOpen(false)
      setDocumentToDelete(null)
      toast.success('Document deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete document')
    },
  })

  const handleUpload = () => {
    if (!uploadFile) {
      toast.error('Please select a file')
      return
    }
    if (!uploadTitle.trim()) {
      toast.error('Please enter a title')
      return
    }
    uploadMutation.mutate()
  }

  const handleDelete = (doc: Document) => {
    setDocumentToDelete(doc)
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = () => {
    if (documentToDelete) {
      deleteMutation.mutate(documentToDelete.id)
    }
  }

  const handleDownload = async (doc: Document) => {
    try {
      const blob = await documentService.downloadDocument(doc.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.file_name
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      toast.error('Failed to download document')
    }
  }

  // Filter documents by search
  const filteredDocuments = documents?.filter(doc =>
    doc.title.toLowerCase().includes(search.toLowerCase()) ||
    doc.description.toLowerCase().includes(search.toLowerCase()) ||
    doc.file_name.toLowerCase().includes(search.toLowerCase())
  ) || []

  if (error) {
    return (
      <div className="p-6">
        <div className="card bg-danger-50 border-danger-200">
          <h3 className="text-lg font-semibold text-danger-800">Error loading documents</h3>
          <p className="text-danger-700 mt-2">{error.message}</p>
          <button 
            onClick={() => refetch()}
            className="btn-secondary mt-4"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-600">Manage documents for legal cases</p>
        </div>
        <button 
          onClick={() => setIsUploadModalOpen(true)}
          className="btn-primary mt-4 md:mt-0 flex items-center"
          disabled={!selectedCaseId}
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload Document
        </button>
      </div>

      {/* Case selection */}
      <div className="card mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Case
        </label>
        <div className="flex items-center space-x-4">
          <select
            value={selectedCaseId || ''}
            onChange={(e) => setSelectedCaseId(Number(e.target.value))}
            className="w-full md:w-1/3"
          >
            <option value="">Select a case...</option>
            {casesData?.cases.map(c => (
              <option key={c.id} value={c.id}>
                {c.case_number} - {c.title}
              </option>
            ))}
          </select>
          {selectedCaseId && (
            <div className="text-sm text-gray-600">
              {casesData?.cases.find(c => c.id === selectedCaseId)?.title}
            </div>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <select className="w-full">
            <option>All Types</option>
            <option>PDF</option>
            <option>Word</option>
            <option>Excel</option>
            <option>Images</option>
          </select>
          <button className="btn-secondary flex items-center justify-center">
            <Filter className="mr-2 h-4 w-4" />
            More Filters
          </button>
        </div>
      </div>

      {/* Documents Grid/Table */}
      <div className="card">
        {!selectedCaseId ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-4 text-gray-500">Please select a case to view documents.</p>
          </div>
        ) : isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-4 text-gray-500">No documents found. Upload your first document.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                          <FileText className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <div className="font-medium text-gray-900">{doc.title}</div>
                          <div className="text-sm text-gray-500">{doc.file_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{doc.description}</div>
                      <div className="text-xs text-gray-500">Version {doc.version} • {doc.mime_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleDownload(doc)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(doc)}
                          className="text-danger-600 hover:text-danger-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button 
                          className="text-gray-600 hover:text-gray-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    className="w-full"
                    placeholder="Document title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    rows={3}
                    className="w-full"
                    placeholder="Document description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File *
                  </label>
                  <div className="mt-1 flex items-center">
                    <label className="cursor-pointer">
                      <span className="btn-secondary flex items-center">
                        <Upload className="mr-2 h-4 w-4" />
                        Choose File
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    {uploadFile && (
                      <span className="ml-3 text-sm text-gray-600">
                        {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                      </span>
                    )}
                  </div>
                </div>
                {uploadProgress > 0 && (
                  <div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary-600 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Uploading... {uploadProgress}%</p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  className="btn-secondary"
                  disabled={uploadMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending || !uploadFile}
                  className="btn-primary"
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && documentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Document</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete "<strong>{documentToDelete.title}</strong>"?
                This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="btn-secondary"
                  disabled={deleteMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="btn-danger"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}